"use client";

import { c as _c } from "react/compiler-runtime";
import { TableCell, TableRow } from "../index";
import { AnimatePresence, motion } from "motion/react";
import React from "react";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import Button from "@/components/ui/button";
import { cx } from "@/lib/tailwind-utils";
import { useAnalyticsGroupedTable } from "./context";
import type { AnalyticsGroup, AnalyticsRow, AnalyticsSummary } from "./types";

// ============================================================================
// AnalyticsDataRow
// ============================================================================

interface AnalyticsDataRowProps<TRow extends AnalyticsRow> {
  row: TRow;
  groupKey?: string;
  index?: number;
}
export function AnalyticsDataRow(t0) {
  const $ = _c(26);
  const {
    row,
    groupKey,
    index
  } = t0;
  const {
    visibleColumns,
    isGrouped,
    onRowClick,
    getRowKey
  } = useAnalyticsGroupedTable();
  let t1;
  if ($[0] !== getRowKey || $[1] !== groupKey || $[2] !== index || $[3] !== row) {
    t1 = getRowKey ? getRowKey(row) : `row-${groupKey}-${index}`;
    $[0] = getRowKey;
    $[1] = groupKey;
    $[2] = index;
    $[3] = row;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const key = t1;
  const t2 = onRowClick && "cursor-pointer hover:bg-row-hover";
  let t3;
  if ($[5] !== t2) {
    t3 = cx("transition-colors duration-200", t2);
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== groupKey || $[8] !== onRowClick || $[9] !== row) {
    t4 = () => onRowClick?.(row, groupKey);
    $[7] = groupKey;
    $[8] = onRowClick;
    $[9] = row;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== isGrouped) {
    t5 = isGrouped && <TableCell />;
    $[11] = isGrouped;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== groupKey || $[14] !== row || $[15] !== visibleColumns) {
    let t7;
    if ($[17] !== groupKey || $[18] !== row) {
      t7 = col => <TableCell key={col.key} className={col.cellClassName}>{col.renderCell(row, groupKey)}</TableCell>;
      $[17] = groupKey;
      $[18] = row;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    t6 = visibleColumns.map(t7);
    $[13] = groupKey;
    $[14] = row;
    $[15] = visibleColumns;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[20] !== key || $[21] !== t3 || $[22] !== t4 || $[23] !== t5 || $[24] !== t6) {
    t7 = <TableRow key={key} className={t3} onClick={t4}>{t5}{t6}</TableRow>;
    $[20] = key;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
}

// ============================================================================
// AnalyticsSummaryRow
// ============================================================================

interface AnalyticsSummaryRowProps<TSummary extends AnalyticsSummary> {
  summary: TSummary;
  label: string;
  isGrandTotal?: boolean;
}
export function AnalyticsSummaryRow(t0) {
  const $ = _c(19);
  const {
    summary,
    label,
    isGrandTotal: t1
  } = t0;
  const isGrandTotal = t1 === undefined ? false : t1;
  const {
    visibleColumns,
    isGrouped,
    renderSummaryLabel
  } = useAnalyticsGroupedTable();
  const t2 = isGrandTotal ? "border-t-2 bg-muted" : "border-t bg-background-secondary";
  let t3;
  if ($[0] !== t2) {
    t3 = cx("font-semibold", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== isGrouped) {
    t4 = isGrouped && <TableCell />;
    $[2] = isGrouped;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== isGrandTotal || $[5] !== label || $[6] !== renderSummaryLabel || $[7] !== summary || $[8] !== visibleColumns) {
    let t6;
    if ($[10] !== isGrandTotal || $[11] !== label || $[12] !== renderSummaryLabel || $[13] !== summary) {
      t6 = (col, index) => <TableCell key={col.key} className={cx(col.cellClassName, isGrandTotal ? "font-bold" : "font-medium")}>{index === 0 ? renderSummaryLabel ? renderSummaryLabel(label, isGrandTotal) : <span className="text-body">{label}</span> : col.renderSummary(summary, isGrandTotal)}</TableCell>;
      $[10] = isGrandTotal;
      $[11] = label;
      $[12] = renderSummaryLabel;
      $[13] = summary;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    t5 = visibleColumns.map(t6);
    $[4] = isGrandTotal;
    $[5] = label;
    $[6] = renderSummaryLabel;
    $[7] = summary;
    $[8] = visibleColumns;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[15] !== t3 || $[16] !== t4 || $[17] !== t5) {
    t6 = <TableRow className={t3}>{t4}{t5}</TableRow>;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}

// ============================================================================
// AnalyticsGroupRow
// ============================================================================

interface AnalyticsGroupRowProps<TRow extends AnalyticsRow, TSummary extends AnalyticsSummary> {
  group: AnalyticsGroup<TRow, TSummary>;
}
export function AnalyticsGroupRow(t0) {
  const $ = _c(80);
  const {
    group
  } = t0;
  const {
    visibleColumns,
    expandedGroups,
    toggleGroup,
    itemLabel,
    onRowClick,
    getRowKey,
    renderGroupHeader,
    pagination
  } = useAnalyticsGroupedTable();
  let T0;
  let T1;
  let isExpanded;
  let isGroupLoading;
  let pagerRow;
  let skeletonRows;
  let t1;
  let t2;
  let t3;
  if ($[0] !== expandedGroups || $[1] !== getRowKey || $[2] !== group || $[3] !== itemLabel || $[4] !== pagination || $[5] !== renderGroupHeader || $[6] !== toggleGroup || $[7] !== visibleColumns) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      isExpanded = expandedGroups.has(group.key);
      const hasPagination = pagination && group.totalPages && group.totalPages > 1;
      let t4;
      if ($[17] !== group.key || $[18] !== pagination) {
        t4 = pagination?.isGroupLoading(group.key) ?? false;
        $[17] = group.key;
        $[18] = pagination;
        $[19] = t4;
      } else {
        t4 = $[19];
      }
      isGroupLoading = t4;
      let t5;
      if ($[20] !== group.count || $[21] !== group.key || $[22] !== group.totalPages || $[23] !== hasPagination || $[24] !== isGroupLoading || $[25] !== itemLabel || $[26] !== pagination || $[27] !== visibleColumns) {
        t5 = hasPagination ? (() => {
          const currentPage = pagination.getGroupPage(group.key);
          const totalPages = group.totalPages ?? 1;
          const start = (currentPage - 1) * pagination.pageSize + 1;
          const end = Math.min(currentPage * pagination.pageSize, group.count);
          return <tr><td colSpan={visibleColumns.length + 1} className="p-0"><div className="flex items-center justify-between border-t border-border px-4 py-1 text-body text-foreground-secondary"><span className="font-semibold">{start.toLocaleString()}-{end.toLocaleString()} of{" "}{group.count.toLocaleString()} {itemLabel}</span><div className="flex items-center gap-1"><Button variant="ghost" icon={HiChevronLeft} onClick={() => pagination.setGroupPage(group.key, currentPage - 1)} disabled={currentPage === 1 || isGroupLoading} aria-label="Previous page" /><span className="px-1 font-semibold tabular-nums">Page {currentPage} of {totalPages}</span><Button variant="ghost" icon={HiChevronRight} onClick={() => pagination.setGroupPage(group.key, currentPage + 1)} disabled={currentPage === totalPages || isGroupLoading} aria-label="Next page" /></div></div></td></tr>;
        })() : null;
        $[20] = group.count;
        $[21] = group.key;
        $[22] = group.totalPages;
        $[23] = hasPagination;
        $[24] = isGroupLoading;
        $[25] = itemLabel;
        $[26] = pagination;
        $[27] = visibleColumns;
        $[28] = t5;
      } else {
        t5 = $[28];
      }
      pagerRow = t5;
      let t6;
      if ($[29] !== group.key || $[30] !== isGroupLoading || $[31] !== pagination?.pageSize || $[32] !== visibleColumns) {
        t6 = isGroupLoading ? Array.from({
          length: pagination?.pageSize ?? 10
        }).map((_, index) => <motion.tr key={`skeleton-${group.key}-${index}`} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="animate-pulse"><td className="w-10" />{visibleColumns.map(_temp)}</motion.tr>) : null;
        $[29] = group.key;
        $[30] = isGroupLoading;
        $[31] = pagination?.pageSize;
        $[32] = visibleColumns;
        $[33] = t6;
      } else {
        t6 = $[33];
      }
      skeletonRows = t6;
      if (renderGroupHeader) {
        let t7;
        if ($[34] !== group.key || $[35] !== toggleGroup) {
          t7 = () => toggleGroup(group.key);
          $[34] = group.key;
          $[35] = toggleGroup;
          $[36] = t7;
        } else {
          t7 = $[36];
        }
        t3 = <React.Fragment key={group.key}>{renderGroupHeader(group, isExpanded, t7)}<AnimatePresence initial={false}>{isExpanded && <>{skeletonRows ?? group.rows.map((row, idx) => <AnalyticsDataRow key={getRowKey ? getRowKey(row) : `row-${group.key}-${idx}`} row={row} groupKey={group.key} index={idx} />)}{pagerRow}</>}</AnimatePresence></React.Fragment>;
        break bb0;
      }
      T1 = React.Fragment;
      t2 = group.key;
      T0 = TableRow;
      t1 = cx("cursor-pointer bg-background-secondary transition-colors hover:bg-hover", isExpanded && "border-b-0");
    }
    $[0] = expandedGroups;
    $[1] = getRowKey;
    $[2] = group;
    $[3] = itemLabel;
    $[4] = pagination;
    $[5] = renderGroupHeader;
    $[6] = toggleGroup;
    $[7] = visibleColumns;
    $[8] = T0;
    $[9] = T1;
    $[10] = isExpanded;
    $[11] = isGroupLoading;
    $[12] = pagerRow;
    $[13] = skeletonRows;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
  } else {
    T0 = $[8];
    T1 = $[9];
    isExpanded = $[10];
    isGroupLoading = $[11];
    pagerRow = $[12];
    skeletonRows = $[13];
    t1 = $[14];
    t2 = $[15];
    t3 = $[16];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[37] !== group.key || $[38] !== toggleGroup) {
    t4 = () => toggleGroup(group.key);
    $[37] = group.key;
    $[38] = toggleGroup;
    $[39] = t4;
  } else {
    t4 = $[39];
  }
  const t5 = isExpanded ? "Collapse group" : "Expand group";
  let t6;
  if ($[40] !== isExpanded) {
    t6 = isExpanded ? <FaChevronDown className="h-3 w-3" /> : <FaChevronRight className="h-3 w-3" />;
    $[40] = isExpanded;
    $[41] = t6;
  } else {
    t6 = $[41];
  }
  let t7;
  if ($[42] !== isExpanded || $[43] !== t5 || $[44] !== t6) {
    t7 = <TableCell className="w-10"><button type="button" className="flex h-6 w-6 items-center justify-center rounded text-foreground-secondary hover:text-foreground" aria-expanded={isExpanded} aria-label={t5}>{t6}</button></TableCell>;
    $[42] = isExpanded;
    $[43] = t5;
    $[44] = t6;
    $[45] = t7;
  } else {
    t7 = $[45];
  }
  let t8;
  if ($[46] !== group.count || $[47] !== group.label || $[48] !== group.summary || $[49] !== itemLabel || $[50] !== visibleColumns) {
    let t9;
    if ($[52] !== group.count || $[53] !== group.label || $[54] !== group.summary || $[55] !== itemLabel) {
      t9 = (col_0, index_0) => <TableCell key={col_0.key} className={cx(index_0 === 0 ? "font-medium" : cx(col_0.cellClassName, "font-medium"))}>{index_0 === 0 ? <div className="flex items-center gap-2"><span className="whitespace-nowrap font-semibold">{group.label}</span>{itemLabel && <span className="whitespace-nowrap text-caption text-foreground-muted">({group.count} {itemLabel})</span>}</div> : col_0.renderSummary(group.summary, false)}</TableCell>;
      $[52] = group.count;
      $[53] = group.label;
      $[54] = group.summary;
      $[55] = itemLabel;
      $[56] = t9;
    } else {
      t9 = $[56];
    }
    t8 = visibleColumns.map(t9);
    $[46] = group.count;
    $[47] = group.label;
    $[48] = group.summary;
    $[49] = itemLabel;
    $[50] = visibleColumns;
    $[51] = t8;
  } else {
    t8 = $[51];
  }
  let t9;
  if ($[57] !== T0 || $[58] !== t1 || $[59] !== t4 || $[60] !== t7 || $[61] !== t8) {
    t9 = <T0 className={t1} onClick={t4}>{t7}{t8}</T0>;
    $[57] = T0;
    $[58] = t1;
    $[59] = t4;
    $[60] = t7;
    $[61] = t8;
    $[62] = t9;
  } else {
    t9 = $[62];
  }
  let t10;
  if ($[63] !== getRowKey || $[64] !== group.key || $[65] !== group.rows || $[66] !== isExpanded || $[67] !== isGroupLoading || $[68] !== onRowClick || $[69] !== pagerRow || $[70] !== skeletonRows || $[71] !== visibleColumns) {
    t10 = isExpanded && <>{isGroupLoading ? skeletonRows : group.rows.map((row_0, idx_0) => <motion.tr key={getRowKey ? getRowKey(row_0) : `row-${group.key}-${idx_0}`} initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: "auto"
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.15
      }} className={cx("bg-background transition-colors duration-200", onRowClick && "cursor-pointer hover:bg-row-hover")} onClick={() => onRowClick?.(row_0, group.key)}><td className="w-10" />{visibleColumns.map(col_1 => <td key={col_1.key} className={cx("truncate py-2 text-body text-foreground-secondary", col_1.cellClassName)}>{col_1.renderCell(row_0, group.key)}</td>)}</motion.tr>)}{pagerRow}</>;
    $[63] = getRowKey;
    $[64] = group.key;
    $[65] = group.rows;
    $[66] = isExpanded;
    $[67] = isGroupLoading;
    $[68] = onRowClick;
    $[69] = pagerRow;
    $[70] = skeletonRows;
    $[71] = visibleColumns;
    $[72] = t10;
  } else {
    t10 = $[72];
  }
  let t11;
  if ($[73] !== t10) {
    t11 = <AnimatePresence initial={false}>{t10}</AnimatePresence>;
    $[73] = t10;
    $[74] = t11;
  } else {
    t11 = $[74];
  }
  let t12;
  if ($[75] !== T1 || $[76] !== t11 || $[77] !== t2 || $[78] !== t9) {
    t12 = <T1 key={t2}>{t9}{t11}</T1>;
    $[75] = T1;
    $[76] = t11;
    $[77] = t2;
    $[78] = t9;
    $[79] = t12;
  } else {
    t12 = $[79];
  }
  return t12;
}
function _temp(col) {
  return <td key={col.key} className={cx("truncate py-2 text-body", col.cellClassName)}><div className="h-4 rounded bg-muted" /></td>;
}